/* eslint-disable react/jsx-pascal-case */
import { useEffect, useRef, useState } from "react";
import { Doughnut, Line } from "react-chartjs-2";
import { toast } from "react-toastify";
import { Chart } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment";
import { Icon } from "@iconify/react";
import { Divider, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import "./DashboardWhatsappInsights.css";
import BarChart_TemplateDelivered from "./BarChart_TemplateDelivered";
import { SeverityPill } from "./SeverityPill";
import Loader from "../Components/Loader";
import { ReactComponent as LegendAUX } from "../Images/LegendAUX.svg";

import {
  getWABAInsightsAgent,
  getWABAInsightsDaily,
} from "../Store/Actions/DashboardAction";

export default function DashboardWhatsappInsights() {
  const initialDateRange = {
    dateStart: moment().subtract(1, "week"),
    dateEnd: moment(),
    tempDateStart: moment().subtract(1, "week"),
    tempDateEnd: moment(),
  };

  const [agentSummaryData, setAgentSummaryData] = useState(null);
  const [chatSummaryData, setChatSummaryData] = useState(null);
  const [dailySummaryData, setDailySummaryData] = useState([]);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [isAgentInsightsLoading, setIsAgentInsightsLoading] = useState(true);
  const [isDailyInsightsLoading, setIsDailyInsightsLoading] = useState(true);
  const [isDateRangeExceeded, setIsDateRangeExceeded] = useState(false);
  const [statusCount, setStatusCount] = useState({});
  const [topTemplatesData, setTopTemplatesData] = useState([]);

  // Ref to always hold the latest dateRange
  const dateRangeRef = useRef(dateRange);

  function fetchDaily(dateStart, dateEnd) {
    const currentDateRange = dateRangeRef.current;

    getWABAInsightsDaily({
      dateStart: moment(dateStart ?? currentDateRange.dateStart).format(
        "YYYY-MM-DD"
      ),
      dateEnd: moment(dateEnd ?? currentDateRange.dateEnd).format("YYYY-MM-DD"),
    })
      .then((response) => {
        if (response.status) {
          setDailySummaryData(response?.data?.daily_summary);
          setTopTemplatesData(response?.data?.top_templates);
          setIsDailyInsightsLoading(false);
        } else {
          console.log("error", response.error);

          toast.error("Error daily insights data: " + response.error, {
            autoClose: 3000,
          });

          setIsDailyInsightsLoading(false);
        }

        setIsDailyInsightsLoading(false);
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Error daily insights data: " + err, { autoClose: 3000 });
        setIsDailyInsightsLoading(false);
      });
  }

  useEffect(() => {
    const fetchData = () => {
      getWABAInsightsAgent()
        .then((response) => {
          if (response.status) {
            setChatSummaryData(response?.data?.chat_waba_summary);
            setAgentSummaryData(response?.data?.agent_waba_summary);

            setStatusCount(
              countChatStatus(
                response?.data?.chat_waba_summary
                  .active_resolved_chat_percentage
              )
            );

            setIsAgentInsightsLoading(false);
          } else {
            console.log("error", response.error);

            toast.error("Error agent insights data: " + response.error, {
              autoClose: 3000,
            });

            setIsAgentInsightsLoading(false);
          }

          setIsAgentInsightsLoading(false);
        })
        .catch((err) => {
          console.log("err", err);
          toast.error("Error agent insights data: " + err, { autoClose: 3000 });
          setIsAgentInsightsLoading(false);
        });

      fetchDaily();
    };

    // Fetch data on first render
    fetchData();

    // Fetch data every ten seconds
    const interval = setInterval(() => {
      fetchData();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Keep ref updated when dateRange changes
  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);

  const dataChatStatus = {
    labels: ["Active", "Resolved"],

    datasets: [
      {
        data: Object.values(statusCount),
        backgroundColor: ["#FF9800", "#2f00ff"],
      },
    ],
  };

  const optionsACDAgents = {
    maintainAspectRatio: false,
    responsive: true,

    plugins: {
      datalabels: {
        font: {
          size: 16,
          weight: 700,
        },

        color: "#fff",
        display: (context) => context.dataset.data[context.dataIndex] > 0,
        formatter: (value) => value + "%",
      },

      legend: {
        display: false,
      },
    },
  };

  const columnsAgentPerformance = [
    {
      field: "full_name",
      flex: 1.5,
      headerName: "Agent Name",
    },

    {
      field: "status",
      flex: 0.75,
      headerName: "Status",

      renderCell: (params) => (
        <SeverityPill color={params.row.status.toLowerCase()}>
          {params.row.status}
        </SeverityPill>
      ),
    },

    {
      field: "status_duration",
      flex: 1.15,
      headerName: "Status Duration",
    },

    {
      field: "total_active_all_time_and_resolved_today",
      flex: 0.85,
      headerName: "Total Chat",
    },

    {
      field: "total_resolved_chat_today",
      flex: 0.75,
      headerName: "Resolved",
    },

    {
      field: "total_active_chat_all_time",
      flex: 0.65,
      headerName: "Pending",
    },

    {
      field: "sla_active_all_time_and_resolved_today",
      flex: 1.65,
      headerName: `SLA Chat (Within ${chatSummaryData?.config_overdue_chat} Days)`,
    },

    {
      field: "aht_chat",
      flex: 0.75,
      headerName: "AHT Chat",
    },

    {
      field: "total_call",
      flex: 0.75,
      headerName: "Total Call",
    },

    {
      field: "aht_call",
      flex: 0.75,
      headerName: "AHT Call",
    },

    {
      field: "total_active_chat_read_no_reply_longest_received",
      flex: 1.35,
      headerName: "Longest to Reply",

      renderCell: (params) =>
        params.row.total_active_chat_read_no_reply_longest_received
          ? moment(
              params.row.total_active_chat_read_no_reply_longest_received
            ).format("DD-MMM-yyyy HH:mm:ss")
          : null,
    },

    {
      field: "total_active_chat_unread_all_time",
      flex: 0.65,
      headerName: "Unread",
    },
  ];

  const countChatStatus = (data) => {
    return {
      active: data?.active_chat_all_time,
      resolved: data?.resolved_chat_today,
    };
  };

  Chart.register(ChartDataLabels);

  let datalabels = {
    color: function (ctx) {
      var bgColor = ctx.dataset.backgroundColor[ctx.dataIndex];
      return bgColor === "#D9D9D9" || bgColor === "#aaaaaa" ? "black" : "white";
    },
    textShadowColor: function (ctx) {
      var bgColor = ctx.dataset.backgroundColor[ctx.dataIndex];
      return bgColor === "#D9D9D9" || bgColor === "#aaaaaa" ? "white" : "black";
    },
    textShadowBlur: 15,
    display: function (context) {
      return context.dataset.data[context.dataIndex] > 0;
    },
    font: {
      weight: "bold",
      size: 15,
    },
    formatter: Math.round,
  };

  const optionsChatVolume = {
    maintainAspectRatio: false,
    responsive: true,

    plugins: {
      datalabels: {
        display: true,
        anchor: "end",
        align: "top",
        offset: 0,
        font: { weight: "bold", size: 12 },
      },
    },

    scales: { y: { grace: "10%" } },
  };

  const dataChatVolume = {
    labels:
      dailySummaryData?.map((data) =>
        moment(data.report_date).format("MMM DD")
      ) || [],

    datasets: [
      {
        backgroundColor: "#4caf50",
        borderColor: "#4caf50",
        data: dailySummaryData.map((data) => data.total_delivered),
        label: "Delivered",
      },

      {
        backgroundColor: "#3f80ff",
        borderColor: "#3f80ff",
        data: dailySummaryData.map((data) => data.total_resolved),
        label: "Resolved",
      },
    ],
  };

  return (
    <div className="dashboard-whatsapp-insights">
      <div className="list_header">
        <div className="list_headerLeft">
          <h1>Whatsapp Insights</h1>
        </div>
      </div>

      <div className="container-dashboard-whatsapp-insights">
        <div className="new-dashboard">
          <section className="cards">
            <div className="item total">
              <div>
                <p className="title">Total Chat Today</p>

                <p className="value">
                  {isAgentInsightsLoading
                    ? "..."
                    : chatSummaryData?.total_active_all_time_and_resolved_today}
                </p>
              </div>

              <Icon
                className="icon"
                height="28"
                icon="bi:chat-left-dots-fill"
                width="28"
              />
            </div>

            <div className="item resolved">
              <div>
                <p className="title">Resolved Today</p>

                <p className="value">
                  {isAgentInsightsLoading
                    ? "..."
                    : chatSummaryData?.total_resolved_chat_waba_today}
                </p>
              </div>

              <Icon
                className="icon"
                height="28"
                icon="akar-icons:check"
                width="28"
              />
            </div>

            <div className="item active">
              <div>
                <p className="title">Active Chat</p>

                <p className="value">
                  {isAgentInsightsLoading
                    ? "..."
                    : chatSummaryData?.total_active_chat_waba_all_time}
                </p>
              </div>

              <Icon
                className="icon"
                height="28"
                icon="bi:chat-left-dots-fill"
                width="28"
              />
            </div>

            <div className="item total">
              <div>
                <p className="title">
                  SLA Achievement (Within {chatSummaryData?.config_overdue_chat}{" "}
                  Days)
                </p>

                <p className="value">
                  {isAgentInsightsLoading
                    ? "..."
                    : chatSummaryData?.sla_active_all_time_and_resolved_today}
                  %
                </p>
              </div>

              <Icon
                className="icon"
                height="28"
                icon="akar-icons:clock"
                width="28"
              />
            </div>

            <div className="item unread">
              <div>
                <p className="title">Unread Chat</p>

                <p className="value">
                  {isAgentInsightsLoading
                    ? "..."
                    : chatSummaryData?.total_active_chat_unread_all_time}
                </p>
              </div>

              <Icon
                className="icon"
                height="28"
                icon="akar-icons:envelope"
                width="28"
              />
            </div>
          </section>

          <section className="chats-detail">
            <div className="insights-daily">
              <div className="filter-item date-range">
                <DatePicker
                  disableFuture
                  label="Start Date"
                  maxDate={dateRange.tempDateEnd}
                  value={dateRange.tempDateStart}
                  renderInput={(params) => <TextField {...params} />}
                  onChange={(newValue) => {
                    setDateRange({
                      ...dateRange,
                      tempDateStart: newValue,
                    });

                    setIsDateRangeExceeded(false);
                  }}
                />

                <Icon color="#7E7E7E" icon="mdi:arrow-expand" width="24" />

                <DatePicker
                  disableFuture
                  label="End Date"
                  minDate={dateRange.tempDateStart}
                  value={dateRange.tempDateEnd}
                  renderInput={(params) => <TextField {...params} />}
                  onChange={(newValue) => {
                    setDateRange({
                      ...dateRange,
                      tempDateEnd: newValue,
                    });

                    setIsDateRangeExceeded(false);
                  }}
                />

                <button
                  className="filter-item filter-button"
                  onClick={() => {
                    if (
                      moment(dateRange.tempDateEnd).diff(
                        moment(dateRange.tempDateStart),
                        "days"
                      ) <= 31
                    ) {
                      setDateRange({
                        ...dateRange,
                        dateStart: dateRange.tempDateStart,
                        dateEnd: dateRange.tempDateEnd,
                      });

                      fetchDaily(
                        dateRange.tempDateStart,
                        dateRange.tempDateEnd
                      );
                    } else {
                      setIsDateRangeExceeded(true);
                    }
                  }}
                >
                  Update
                </button>

                <p className={isDateRangeExceeded ? "show" : ""}>
                  Date range exceeded! Please keep the date range between 31
                  days.
                </p>
              </div>

              <div className="insights-daily-charts">
                {isDailyInsightsLoading ? (
                  <Loader />
                ) : (
                  <>
                    <div className="item chat-volume">
                      <header>
                        <div className="title">Daily Chat Volume</div>
                      </header>

                      <Divider />

                      <Line
                        data={dataChatVolume}
                        className="table"
                        options={optionsChatVolume}
                        plugins={[ChartDataLabels]}
                      />
                    </div>

                    <div className="item template-delivered">
                      <header>
                        <div className="title">Template Message Delivered</div>
                      </header>

                      <Divider />

                      <BarChart_TemplateDelivered
                        templateDelivered={topTemplatesData}
                        datalabels={datalabels}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="chat-status-chart">
              <header>
                <div className="title">Chat Status</div>
              </header>

              <div className="chart-container">
                <Doughnut
                  data={dataChatStatus}
                  options={optionsACDAgents}
                  plugins={[ChartDataLabels]}
                />
              </div>

              <div className="chat-status-chart-legend">
                <div className="active">
                  <LegendAUX />
                  <span>Active</span>
                  <span className="count">{statusCount.active}%</span>
                </div>

                <div className="resolved">
                  <Icon
                    height="26"
                    icon="tabler:square-rounded-check"
                    width="26"
                  />

                  <span>Resolved</span>
                  <span className="count">{statusCount.resolved}%</span>
                </div>
              </div>
            </div>
          </section>

          <div className="acd-agents">
            <header>
              <div className="title">
                <p>Agent Performance</p>
              </div>
            </header>

            <div className="table-container">
              <DataGrid
                className="table"
                columns={columnsAgentPerformance}
                disableColumnFilter
                disableColumnMenu
                disableSelectionOnClick
                hideFooter
                loading={isAgentInsightsLoading}
                rows={agentSummaryData ?? []}
                getRowId={(row) => row.agent_id}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
